import { jwtDecode } from 'jwt-decode';

import { AuthStateMachine } from './AuthStateMachine';
import { StorageAdapter } from './StorageAdapter';
import type { User, AuthTokens, AuthState, JWTPayload, LoginCredentials } from './types';
import { getApiBaseUrl, isLoginHostForClient } from '../../config/runtimeUrls';

type AuthSubscriber = (state: AuthState, user: User | null) => void;

// 🔥 HMR-Safe: Store singleton in window object to persist across hot reloads
declare global {
  interface Window {
    __authServiceInstance?: AuthService;
  }
}

export class AuthService {
  private static instance: AuthService | null = null;

  private stateMachine: AuthStateMachine;
  private storage: StorageAdapter;
  private tokens: AuthTokens | null = null;
  private user: User | null = null;

  private subscribers: Set<AuthSubscriber> = new Set();

  private refreshPromise: Promise<boolean> | null = null;
  private tokenClearingPaused: boolean = false;
  private tokenClearInterval: NodeJS.Timeout | null = null;
  private initializePromise: Promise<boolean> | null = null;
  private initComplete: boolean = false;
  private authContinueInProgress: boolean = false;

  private readonly TOKEN_EXPIRY_GRACE_SECONDS = 60;
  private readonly TOKEN_CLEAR_INTERVAL_MS = 60_000;

  private constructor() {
    this.stateMachine = new AuthStateMachine();
    this.storage = new StorageAdapter();
    console.log('🔐 AuthService: New instance created');
  }

  private isCentralLoginRoute(): boolean {
    if (typeof window === 'undefined') return false;
    if (!isLoginHostForClient()) return false;
    const path = String(window.location.pathname || '').toLowerCase();
    return path === '/' || path === '/login' || path.startsWith('/login/');
  }

  public forceUnauthenticated(reason: string = 'manual'): void {
    this.clearAuthState();
    this.stateMachine.setState('UNAUTHENTICATED');
    this.notifySubscribers();
    try {
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('postLoginRedirect');
      }
    } catch (_) {}
    if (process.env.NODE_ENV !== 'production') {
      console.info('🔐 AuthService: Forced UNAUTHENTICATED state', { reason });
    }
  }

  public async clearSharedSessionCookie(): Promise<void> {
    try {
      const backendUrl = getApiBaseUrl();
      await fetch(`${backendUrl}/auth/logout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({}),
      });
    } catch (error) {
      console.warn('🔐 AuthService: Failed to clear shared session cookie', error);
    }
  }

  public static getInstance(): AuthService {
    // 🔥 HMR-Safe: Check window object first
    if (typeof window !== 'undefined' && window.__authServiceInstance) {
      console.log('🔐 AuthService: Using existing instance from window (HMR preserved)');
      AuthService.instance = window.__authServiceInstance;
      return AuthService.instance;
    }

    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
      // Store in window for HMR persistence
      if (typeof window !== 'undefined') {
        window.__authServiceInstance = AuthService.instance;
      }
    }
    return AuthService.instance;
  }

  public async initialize(): Promise<boolean> {
    // Enterprise guardrail:
    // Never auto-hydrate sessions on central login host. This prevents silent
    // cross-account re-entry and forces explicit tenant discovery.
    if (this.isCentralLoginRoute() && !this.authContinueInProgress) {
      this.forceUnauthenticated('central_login_route');
      this.initComplete = true;
      return false;
    }

    // 🔥 HMR-Safe: Skip re-initialization if already authenticated
    if (this.isAuthenticated()) {
      console.log('🔐 AuthService: Already authenticated, skipping initialization (HMR preserved)');
      this.initComplete = true;
      return true;
    }

    // Deduplicate concurrent initialize calls (React Strict Mode, hot reload)
    if (this.initializePromise) {
      console.log('🔐 AuthService: Initialization already in progress, returning existing promise');
      return this.initializePromise;
    }

    console.log('🔐 AuthService: Initializing...');

    this.initializePromise = (async () => {
      try {
        // Only transition to INITIALIZING if not already there
        if (this.stateMachine.getState() !== 'INITIALIZING') {
          this.stateMachine.transition('INITIALIZING');
        }

        const storedTokens = this.storage.getTokens();
        const storedUser = this.storage.getUser();

        if (!storedTokens || !storedUser) {
          console.log(
            '🔐 AuthService: No stored credentials found, attempting refresh via shared cookie...'
          );
          const recoveredFromCookie = await this.refresh();
          if (recoveredFromCookie) {
            console.log('🔐 AuthService: Session restored from refresh cookie');
            return true;
          }
          this.stateMachine.transition('UNAUTHENTICATED');
          this.notifySubscribers();
          return false;
        }

        if (this.isTokenExpired(storedTokens.accessToken, this.TOKEN_EXPIRY_GRACE_SECONDS)) {
          console.log('🔐 AuthService: Access token expired, attempting refresh...');

          const refreshed = await this.refresh();
          if (!refreshed) {
            console.log('🔐 AuthService: Refresh failed, clearing auth');
            this.clearAuthState();
            this.stateMachine.transition('UNAUTHENTICATED');
            this.notifySubscribers();
            return false;
          }

          console.log('🔐 AuthService: Token refreshed successfully');
          return true;
        }

        this.tokens = storedTokens;
        this.user = storedUser;
        this.stateMachine.transition('AUTHENTICATED');
        this.startTokenClearInterval();
        this.notifySubscribers();

        console.log('🔐 AuthService: Initialized with valid credentials', {
          userId: this.user.id,
          role: this.user.role,
        });

        return true;
      } catch (error) {
        console.error('🔐 AuthService: Initialization failed', error);
        this.clearAuthState();
        this.stateMachine.transition('UNAUTHENTICATED');
        this.notifySubscribers();
        return false;
      } finally {
        this.initComplete = true;
        this.initializePromise = null;
      }
    })();

    return this.initializePromise;
  }

  public async login(credentials: LoginCredentials): Promise<{
    success: boolean;
    error?: string;
    user?: User | null;
    accessToken?: string;
  }> {
    console.log('🔐 AuthService: Login attempt', { email: credentials.email });
    console.log('🔐 AuthService: Current state:', this.stateMachine.getState());

    // If already authenticated from a restored session, return that session
    // rather than forcing a logout/login cycle.
    if (this.stateMachine.getState() === 'AUTHENTICATED') {
      console.log('🔐 AuthService: Already authenticated, returning existing session');
      return {
        success: true,
        user: this.user,
        accessToken: this.tokens?.accessToken || undefined,
      };
    }

    if (!this.stateMachine.canTransition('AUTHENTICATING')) {
      const currentState = this.stateMachine.getState();
      console.error('🔐 AuthService: Cannot transition to AUTHENTICATING from', currentState);
      return { success: false, error: `Invalid auth state for login (current: ${currentState})` };
    }

    this.stateMachine.transition('AUTHENTICATING');

    try {
      // ENTERPRISE V2: Direct backend API call (no apiClient to avoid circular dependency)
      const backendUrl = getApiBaseUrl();
      const response = await fetch(`${backendUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        // Ensure refresh cookie is accepted in dev/prod when backend sets it
        credentials: 'include',
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        let errorMessage = 'Login failed';
        try {
          const error = await response.json();
          const normalizedError = error?.error;
          errorMessage =
            error?.message ||
            (typeof normalizedError === 'string'
              ? normalizedError
              : normalizedError?.message) ||
            'Login failed';
        } catch {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        this.stateMachine.transition('UNAUTHENTICATED');
        return { success: false, error: errorMessage };
      }

      const data = await response.json();
      // Backend responses may be normalized as { success, data, error }.
      const tokenData = data?.data && typeof data.data === 'object' ? data.data : data;

      const tokens: AuthTokens = {
        accessToken: tokenData?.accessToken || tokenData?.token || '',
        refreshToken: tokenData?.refreshToken || '',
      };

      if (!tokens.accessToken) {
        this.stateMachine.transition('UNAUTHENTICATED');
        return { success: false, error: 'No access token received from server' };
      }

      const user: User = this.extractUserFromToken(tokens.accessToken);
      user.tenantSlug =
        tokenData?.tenantSlug || tokenData?.user?.tenantSlug || user.tenantSlug;
      if (!user.tenantId) {
        user.tenantId = tokenData?.tenantId || tokenData?.user?.tenantId;
      }

      this.tokens = tokens;
      this.user = user;
      this.storage.setTokens(tokens);
      this.storage.setUser(user);

      this.stateMachine.transition('AUTHENTICATED');
      this.startTokenClearInterval();
      this.notifySubscribers();

      console.log('🔐 AuthService V2: Login successful', {
        userId: user.id,
        role: user.role,
        hasRefreshToken: !!tokens.refreshToken,
      });

      return { success: true, user, accessToken: tokens.accessToken };
    } catch (error: any) {
      console.error('🔐 AuthService V2: Login error', error);
      this.stateMachine.transition('UNAUTHENTICATED');
      return { success: false, error: error.message || 'Network error' };
    }
  }

  public async logout(): Promise<void> {
    console.log('🔐 AuthService V2: Logout initiated');
    let logoutTargets: string[] = [];

    try {
      const backendUrl = getApiBaseUrl();
      if (this.tokens?.refreshToken) {
        const response = await fetch(`${backendUrl}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.tokens.accessToken}`,
          },
          credentials: 'include',
          body: JSON.stringify({ refreshToken: this.tokens.refreshToken }),
        });
        const payload = await response.json().catch(() => ({}));
        const normalized = payload?.data && typeof payload.data === 'object' ? payload.data : payload;
        if (Array.isArray(normalized?.logoutTargets)) {
          logoutTargets = normalized.logoutTargets;
        }
      } else {
        // Still call logout endpoint to clear refresh cookie server-side if present.
        const response = await fetch(`${backendUrl}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(this.tokens?.accessToken
              ? { Authorization: `Bearer ${this.tokens.accessToken}` }
              : {}),
          } as any,
          credentials: 'include',
          body: JSON.stringify({}),
        });
        const payload = await response.json().catch(() => ({}));
        const normalized = payload?.data && typeof payload.data === 'object' ? payload.data : payload;
        if (Array.isArray(normalized?.logoutTargets)) {
          logoutTargets = normalized.logoutTargets;
        }
      }
    } catch (error) {
      console.warn(
        '🔐 AuthService V2: Logout API call failed (proceeding with local cleanup)',
        error
      );
    }

    try {
      await this.runGlobalLogoutFanout(logoutTargets);
    } catch (error) {
      console.warn('🔐 AuthService V2: Cross-host logout fan-out failed', error);
    }

    this.clearAuthState();
    this.stateMachine.transition('LOGGED_OUT');
    this.notifySubscribers();

    console.log('🔐 AuthService V2: Logout complete');
  }

  private async runGlobalLogoutFanout(targets: string[] = []): Promise<void> {
    const uniqueTargets = Array.from(
      new Set(
        (Array.isArray(targets) ? targets : [])
          .map(target => String(target || '').trim())
          .filter(Boolean)
      )
    );
    if (!uniqueTargets.length) return;

    await Promise.allSettled(
      uniqueTargets.map(target =>
        fetch(target, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({}),
          keepalive: true,
        })
      )
    );
  }

  public async refresh(): Promise<boolean> {
    if (this.refreshPromise) {
      console.log('🔐 AuthService: Refresh already in progress, waiting...');
      return this.refreshPromise;
    }

    if (!this.stateMachine.canTransition('REFRESHING')) {
      console.warn('🔐 AuthService: Cannot refresh in current state');
      return false;
    }

    console.log('🔐 AuthService: Token refresh initiated');

    this.refreshPromise = (async () => {
      const previousState = this.stateMachine.getState();
      this.stateMachine.transition('REFRESHING');

      try {
        const refreshToken =
          this.tokens?.refreshToken || this.storage.getTokens()?.refreshToken || '';

        const backendUrl = getApiBaseUrl();
        const response = await fetch(`${backendUrl}/auth/refresh-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // Allow HTTP-only refreshToken cookie to be used when present
          credentials: 'include',
          body: refreshToken ? JSON.stringify({ refreshToken }) : JSON.stringify({}),
        });

        if (!response.ok) {
          console.error('🔐 AuthService: Refresh failed', response.status);
          // ENTERPRISE SAFETY:
          // Do NOT clear a valid access token just because refresh failed (e.g., no refresh token stored).
          // Only clear if the current access token is missing/expired.
          const currentAccess =
            this.tokens?.accessToken || this.storage.getTokens()?.accessToken || '';
          const hasValidAccess = Boolean(currentAccess) && !this.isTokenExpired(currentAccess, 0);
          if (!hasValidAccess) {
            this.clearAuthState();
            this.stateMachine.transition('UNAUTHENTICATED');
            this.notifySubscribers();
          } else {
            // Restore previous state and keep the session alive
            this.stateMachine.setState(previousState);
            this.notifySubscribers();
          }
          return false;
        }

        const data = await response.json();

        // Backend returns tokens in data.data object
        const tokenData = data.data || data;

        const newTokens: AuthTokens = {
          accessToken: tokenData.accessToken,
          refreshToken: tokenData.refreshToken || refreshToken,
        };

        const user = this.extractUserFromToken(newTokens.accessToken);
        user.tenantSlug =
          tokenData?.tenantSlug ||
          tokenData?.user?.tenantSlug ||
          this.user?.tenantSlug ||
          this.storage.getUser()?.tenantSlug;
        if (!user.tenantId) {
          user.tenantId = tokenData?.tenantId || tokenData?.user?.tenantId;
        }

        this.tokens = newTokens;
        this.user = user;
        this.storage.setTokens(newTokens);
        this.storage.setUser(user);

        if (this.stateMachine.canTransition('AUTHENTICATED')) {
          this.stateMachine.transition('AUTHENTICATED');
        } else {
          this.stateMachine.setState('AUTHENTICATED');
        }

        this.notifySubscribers();

        console.log('🔐 AuthService: Token refresh successful');
        return true;
      } catch (error) {
        console.error('🔐 AuthService: Refresh error', error);
        this.clearAuthState();
        this.stateMachine.transition('UNAUTHENTICATED');
        this.notifySubscribers();
        return false;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  public pauseTokenClear(): void {
    console.log('🔐 AuthService: Token clearing paused for OAuth');
    this.tokenClearingPaused = true;
  }

  public resumeTokenClear(): void {
    console.log('🔐 AuthService: Token clearing resumed');
    this.tokenClearingPaused = false;
  }

  public serializeState(): string {
    console.log('🔐 AuthService: serializeState called');
    console.log('🔐 AuthService: Current state before serialization:', {
      hasTokens: !!this.tokens,
      hasAccessToken: !!this.tokens?.accessToken,
      hasRefreshToken: !!this.tokens?.refreshToken,
      accessTokenPreview: this.tokens?.accessToken?.substring(0, 20) + '...',
      hasUser: !!this.user,
      userId: this.user?.id,
      stateMachineState: this.stateMachine.getState(),
    });

    const state = {
      tokens: this.tokens,
      user: this.user,
      stateMachineState: this.stateMachine.getState(),
      timestamp: Date.now(),
    };

    const serialized = JSON.stringify(state);
    console.log('🔐 AuthService: Serialized state length:', serialized.length);

    return serialized;
  }

  public deserializeState(serialized: string | null): void {
    console.log('🔐 AuthService: deserializeState called', {
      hasSerializedState: !!serialized,
      length: serialized?.length,
    });

    if (!serialized) {
      console.warn('🔐 AuthService: No serialized state to restore');
      return;
    }

    try {
      const state = JSON.parse(serialized);
      console.log('🔐 AuthService: Parsed serialized state', {
        hasTokens: !!state.tokens,
        hasAccessToken: !!state.tokens?.accessToken,
        hasRefreshToken: !!state.tokens?.refreshToken,
        hasUser: !!state.user,
        userId: state.user?.id,
        stateMachineState: state.stateMachineState,
        age: Date.now() - state.timestamp,
      });

      const age = Date.now() - state.timestamp;
      if (age > 600_000) {
        console.warn('🔐 AuthService: Serialized state too old, ignoring', { age });
        return;
      }

      // CRITICAL: Set tokens first
      this.tokens = state.tokens;
      this.user = state.user;

      console.log('🔐 AuthService: Set this.tokens =', {
        hasTokens: !!this.tokens,
        hasAccessToken: !!this.tokens?.accessToken,
        accessTokenPreview: this.tokens?.accessToken?.substring(0, 20) + '...',
      });
      console.log('🔐 AuthService: Set this.user =', {
        hasUser: !!this.user,
        userId: this.user?.id,
      });

      if (state.tokens && state.user) {
        console.log('🔐 AuthService: Restoring tokens and user to storage...');
        this.storage.setTokens(state.tokens);
        this.storage.setUser(state.user);
        this.stateMachine.setState(state.stateMachineState || 'AUTHENTICATED');
        console.log(
          '🔐 AuthService: Storage updated, state machine set to',
          this.stateMachine.getState()
        );
      } else {
        console.error('🔐 AuthService: Missing tokens or user in serialized state!', {
          hasTokens: !!state.tokens,
          hasUser: !!state.user,
        });
      }

      this.notifySubscribers();
      console.log('🔐 AuthService: State restored from OAuth serialization ✅');

      // Verify restoration - check BOTH this.tokens and storage
      console.log('🔐 AuthService: Verification after restore:');
      console.log('  - this.tokens:', {
        exists: !!this.tokens,
        hasAccessToken: !!this.tokens?.accessToken,
      });
      console.log('  - this.getAccessToken():', !!this.getAccessToken());
      console.log('  - storage.getTokens():', !!this.storage.getTokens()?.accessToken);
    } catch (error) {
      console.error('🔐 AuthService: Failed to deserialize state', error);
    }
  }

  /**
   * MIGRATION HELPER: Sync auth state from legacy AuthContext
   * This allows gradual migration from old auth system to new AuthService V2
   */
  public syncLegacyAuth(tokens: AuthTokens, user: User): void {
    console.log('🔐 AuthService V2: Syncing from legacy AuthContext', {
      userId: user.id,
      hasAccessToken: !!tokens.accessToken,
      hasRefreshToken: !!tokens.refreshToken,
    });

    this.tokens = tokens;
    this.user = user;

    // Persist to V2 storage
    this.storage.setTokens(tokens);
    this.storage.setUser(user);

    // Update state machine
    if (this.stateMachine.canTransition('AUTHENTICATED')) {
      this.stateMachine.transition('AUTHENTICATED');
    } else {
      this.stateMachine.setState('AUTHENTICATED');
    }

    // Start token monitoring
    this.startTokenClearInterval();

    // Notify subscribers
    this.notifySubscribers();

    this.initComplete = true;
    console.log('🔐 AuthService V2: Legacy sync complete');
  }

  public getState(): AuthState {
    return this.stateMachine.getState();
  }

  public isAuthenticated(): boolean {
    return this.stateMachine.getState() === 'AUTHENTICATED';
  }

  public isInitComplete(): boolean {
    return this.initComplete;
  }

  public setAuthContinueInProgress(active: boolean): void {
    this.authContinueInProgress = Boolean(active);
  }

  public isAuthContinueInProgress(): boolean {
    return this.authContinueInProgress;
  }

  public getUser(): User | null {
    return this.user;
  }

  public getAccessToken(): string | null {
    const stored = this.storage.getTokens()?.accessToken;
    const raw = this.tokens?.accessToken || stored;
    const token = raw ? String(raw).trim() : '';
    const lower = token.toLowerCase();
    // ✅ ENTERPRISE FIX: Never return literal "null"/"undefined" (prevents Authorization: Bearer null)
    const isJwtLike = token.split('.').length >= 3;
    if (!token || lower === 'null' || lower === 'undefined' || !isJwtLike) {
      // Clear stale/bad state to stop retry storms/polling loops (only if token exists)
      if (raw) {
        try {
          this.clearAuthState();
        } catch {}
      }
      return null;
    }
    return token;
  }

  public subscribe(callback: AuthSubscriber): () => void {
    this.subscribers.add(callback);
    callback(this.stateMachine.getState(), this.user);
    return () => {
      this.subscribers.delete(callback);
    };
  }

  private isTokenExpired(token: string, graceSeconds: number = 0): boolean {
    try {
      const decoded = jwtDecode<JWTPayload>(token);
      const now = Math.floor(Date.now() / 1000);
      return now > decoded.exp + graceSeconds;
    } catch (error) {
      console.warn('🔐 AuthService: Failed to decode token', error);
      return true;
    }
  }

  private extractUserFromToken(accessToken: string): User {
    const decoded = jwtDecode<JWTPayload>(accessToken);

    return {
      id: decoded.sub,
      email: decoded.email,
      name: decoded.name || decoded.email,
      role: decoded.role as any,
      systemRole: decoded.systemRole,
      tenantId: decoded.tenantId,
      tenantSlug: decoded.tenantSlug,
      permissions: decoded.permissions || [],
      isImpersonated: Boolean(decoded.is_impersonated),
      impersonatorId: decoded.impersonator_id,
    };
  }

  private clearAuthState(): void {
    this.tokens = null;
    this.user = null;
    this.storage.clear();
    this.stopTokenClearInterval();
  }

  private notifySubscribers(): void {
    const state = this.stateMachine.getState();
    this.subscribers.forEach(callback => {
      try {
        callback(state, this.user);
      } catch (error) {
        console.error('🔐 AuthService: Subscriber callback error', error);
      }
    });
  }

  private startTokenClearInterval(): void {
    if (this.tokenClearInterval) {
      return;
    }

    this.tokenClearInterval = setInterval(() => {
      if (this.tokenClearingPaused) {
        console.log('🔐 AuthService: Token clear check skipped (paused for OAuth)');
        return;
      }

      if (!this.tokens?.accessToken) {
        return;
      }

      if (this.isTokenExpired(this.tokens.accessToken, this.TOKEN_EXPIRY_GRACE_SECONDS)) {
        console.log('🔐 AuthService: Access token expired, triggering refresh');
        this.refresh();
      }
    }, this.TOKEN_CLEAR_INTERVAL_MS);

    console.log('🔐 AuthService: Token clear interval started');
  }

  private stopTokenClearInterval(): void {
    if (this.tokenClearInterval) {
      clearInterval(this.tokenClearInterval);
      this.tokenClearInterval = null;
      console.log('🔐 AuthService: Token clear interval stopped');
    }
  }
}

export const authService = AuthService.getInstance();
